import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { useMoneyFormatter } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { toast } from "sonner";

export default function SavingsPage() {
  const fmt = useMoneyFormatter();
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "COMPANY_ADMIN";
  const [accts, setAccts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", interest_rate: 0, balance: "", amount: "", start_date: "" });
  const [active, setActive] = useState(null); // selected account for txns
  const [txns, setTxns] = useState([]);
  const [tx, setTx] = useState({ type: "DEPOSIT", amount: 0, note: "", transaction_date: new Date().toISOString().slice(0, 10) });
  const [editingId, setEditingId] = useState(null);
  const [editingAcct, setEditingAcct] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/savings/accounts"), api.get("/customers")]);
    setAccts(a.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active) api.get(`/savings/transactions/${active.id}`).then(({ data }) => setTxns(data));
  }, [active]);

  const create = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = {};
        // interest_rate only if changed
        if (Number(form.interest_rate) !== editingAcct.interest_rate) {
          payload.interest_rate = Number(form.interest_rate);
        }
        // customer_id only changes if the account is unused (balance===0 & no txns)
        if (editingAcct && (editingAcct.balance || 0) === 0) {
          payload.customer_id = form.customer_id;
        }
        // Balance override — Company Admin only and only when a value was provided
        if (isCompanyAdmin && form.balance !== "" && form.balance !== null && form.balance !== undefined) {
          const b = Number(form.balance);
          if (!Number.isNaN(b)) payload.balance = b;
        }
        await api.patch(`/savings/accounts/${editingId}`, payload);
        toast.success("Savings account updated");
      } else {
        const payload = {
          customer_id: form.customer_id,
          interest_rate: Number(form.interest_rate),
        };
        if (form.amount !== "" && form.amount !== null && form.amount !== undefined) {
          payload.amount = Number(form.amount);
        }
        if (form.start_date) {
          payload.start_date = new Date(form.start_date + "T12:00:00Z").toISOString();
        }
        await api.post("/savings/accounts", payload);
        toast.success("Savings account opened");
      }
      setOpen(false); setEditingId(null); setEditingAcct(null);
      setForm({ customer_id: "", interest_rate: 0, balance: "", amount: "", start_date: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const startEdit = (a, ev) => {
    if (ev) ev.stopPropagation();
    setEditingId(a.id);
    setEditingAcct(a);
    setForm({ customer_id: a.customer_id, interest_rate: a.interest_rate, balance: a.balance ?? "", amount: a.balance ?? "", start_date: a.start_date ? String(a.start_date).slice(0, 10) : "" });
    setOpen(true);
  };

  const remove = async (a, ev) => {
    if (ev) ev.stopPropagation();
    if (!window.confirm("Delete this unused savings account? This action cannot be undone.")) return;
    try {
      await api.delete(`/savings/accounts/${a.id}`);
      toast.success("Account deleted");
      if (active?.id === a.id) setActive(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const trans = async (e) => {
    e.preventDefault();
    if (!active) return;
    try {
      await api.post("/savings/transactions", {
        account_id: active.id,
        type: tx.type,
        amount: Number(tx.amount),
        note: tx.note,
        transaction_date: tx.transaction_date ? new Date(tx.transaction_date + "T12:00:00Z").toISOString() : null,
      });
      toast.success(tx.type + " recorded");
      setTx({ type: "DEPOSIT", amount: 0, note: "", transaction_date: new Date().toISOString().slice(0, 10) });
      const [a, t] = await Promise.all([api.get("/savings/accounts"), api.get(`/savings/transactions/${active.id}`)]);
      setAccts(a.data); setTxns(t.data);
      const updated = a.data.find((x) => x.id === active.id);
      if (updated) setActive(updated);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const refreshAfterTxnChange = async () => {
    const [a, t] = await Promise.all([
      api.get("/savings/accounts"),
      api.get(`/savings/transactions/${active.id}`),
    ]);
    setAccts(a.data); setTxns(t.data);
    const updated = a.data.find((x) => x.id === active.id);
    if (updated) setActive(updated);
  };

  const editTxn = async (t) => {
    const raw = window.prompt(
      `Edit amount for this ${t.type} (current: ${t.amount}). Enter new amount:`,
      String(t.amount),
    );
    if (raw === null) return;
    const next = Number(raw);
    if (!Number.isFinite(next) || next <= 0) {
      toast.error("Amount must be a positive number");
      return;
    }
    if (Math.abs(next - Number(t.amount)) < 0.005) return;
    try {
      await api.patch(`/savings/transactions/${t.id}`, { amount: next });
      toast.success("Transaction updated");
      await refreshAfterTxnChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const deleteTxn = async (t) => {
    if (!window.confirm(
      `Delete this ${t.type} of ${t.amount}? Account balance and the linked journal entry will be recomputed.`,
    )) return;
    try {
      await api.delete(`/savings/transactions/${t.id}`);
      toast.success("Transaction deleted");
      await refreshAfterTxnChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const inputCls = "w-full bg-white border border-border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm";

  const customerName = (id) => customers.find((c) => c.id === id)?.name || id?.slice(0, 8);

  const filteredAndSortedAccts = accts
    .filter((a) => {
      const custName = customerName(a.customer_id).toLowerCase();
      return custName.includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      // Sort ACTIVE accounts first
      if (a.status === "ACTIVE" && b.status !== "ACTIVE") return -1;
      if (a.status !== "ACTIVE" && b.status === "ACTIVE") return 1;
      // Then sort by most recent (newest first)
      return new Date(b.created_at) - new Date(a.created_at);
    });

  return (
    <div data-testid="savings-page">
      <PageHeader title="Savings" subtitle="Accounts & transactions"
        actions={<button onClick={() => { setEditingId(null); setEditingAcct(null); setForm({ customer_id: "", interest_rate: 0, balance: "", amount: "", start_date: "" }); setOpen(true); }} className="btn-primary" data-testid="new-savings-btn">Open account</button>} />

      {open && (
        <Section title={editingId ? "Edit savings account" : "Open new account"} testId="new-savings-form" actions={<button onClick={() => { setOpen(false); setEditingId(null); setEditingAcct(null); }} className="btn-ghost">Cancel</button>}>
          <form onSubmit={create} className="grid md:grid-cols-2 gap-4">
            <div><label className="label block mb-1">Customer</label>
              <select required className={inputCls} value={form.customer_id} disabled={!!editingId && editingAcct && (editingAcct.balance || 0) !== 0} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} data-testid="savings-customer">
                <option value="">— Select —</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
              </select>
            </div>
            <div><label className="label block mb-1">Opening Amount</label>
              <input type="number" step="0.01" min="0" className={inputCls} value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="savings-amount" />
            </div>
            <div><label className="label block mb-1">Interest Rate (%)</label>
              <input type="number" step="0.01" className={inputCls} value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} data-testid="savings-interest" />
            </div>
            <div>
              <label className="label block mb-1">Start Date</label>
              <input type="date" className={inputCls} value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="savings-start-date" />
              <div className="text-xs text-muted-foreground mt-1">Optional; used as the account’s opening reference date.</div>
            </div>
            {editingId && isCompanyAdmin && (
              <div className="md:col-span-2">
                <label className="label block mb-1">Balance (admin override)</label>
                <input type="number" step="0.01" min="0" className={inputCls} value={form.balance}
                  onChange={(e) => setForm({ ...form, balance: e.target.value })} data-testid="savings-balance" />
                <div className="text-xs text-muted-foreground mt-1">
                  Direct balance override. Use to correct opening balances or reconcile errors. No transaction is recorded.
                </div>
              </div>
            )}
            <div className="md:col-span-2 flex justify-end"><button type="submit" className="btn-primary" data-testid="savings-submit-btn">{editingId ? "Save changes" : "Open account"}</button></div>
          </form>
        </Section>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <Section title={`Accounts (${accts.length})`} testId="accounts-list">
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search by customer name..."
              className={inputCls}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="savings-search"
            />
          </div>
          {filteredAndSortedAccts.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center" data-testid="empty-savings">No savings accounts found.</div>
          ) : (
            <ul className="space-y-2">
  {filteredAndSortedAccts.map((a) => {
    const canEditRow = (a.balance || 0) === 0;
    const canDeleteRow = canEditRow;
    const showEdit = canEditRow || isCompanyAdmin;
    return (
      <li key={a.id}>
        <div className={`p-3 rounded-md border transition-colors ${active?.id === a.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
          <button onClick={() => setActive(a)} className="w-full text-left" data-testid={`account-${a.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs text-muted-foreground">{a.account_number}</div>
                <div className="font-medium">{customerName(a.customer_id)}</div>
              </div>
              <div className="text-right">
                <div className="metric text-xl">{fmt(a.balance)}</div>
                <Pill variant={a.status === "ACTIVE" ? "success" : "default"}>{a.status}</Pill>
              </div>
            </div>
            {a.start_date && (
              <div className="text-xs text-muted-foreground mt-1">Opened {fmtDate(a.start_date)}</div>
            )}
          </button>
          {showEdit && (
            <div className="flex justify-end gap-2 mt-2 pt-2 border-t border-border">
              <button className="btn-ghost text-xs px-2 py-1" onClick={(ev) => startEdit(a, ev)} data-testid={`edit-account-${a.id}`}>Edit</button>
              {canDeleteRow && (
                <button className="btn-ghost text-xs px-2 py-1 text-destructive" onClick={(ev) => remove(a, ev)} data-testid={`delete-account-${a.id}`}>Delete</button>
              )}
            </div>
          )}
        </div>
      </li>
    );
  })}
</ul>
          )}
        </Section>

        <div className="lg:col-span-2">
          {active ? (
            <>
              <Section title={`Transactions · ${active.account_number}`} testId="txns-section">
                <form onSubmit={trans} className="grid md:grid-cols-5 gap-3 mb-4">
                  <input type="date" className={inputCls} value={tx.transaction_date} onChange={(e) => setTx({ ...tx, transaction_date: e.target.value })} data-testid="tx-date" />
                  <select className={inputCls} value={tx.type} onChange={(e) => setTx({ ...tx, type: e.target.value })} data-testid="tx-type">
                    <option value="DEPOSIT">Deposit</option>
                    <option value="WITHDRAWAL">Withdrawal</option>
                  </select>                  
                  <input type="number" step="0.01" min="0.01" required placeholder="Amount" className={inputCls} value={tx.amount} onChange={(e) => setTx({ ...tx, amount: e.target.value })} data-testid="tx-amount" />
                  <input placeholder="Note (optional)" className={inputCls} value={tx.note} onChange={(e) => setTx({ ...tx, note: e.target.value })} data-testid="tx-note" />
                  <button className="btn-primary" data-testid="tx-submit-btn">Record</button>
                </form>
                {txns.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-6 text-center">No transactions yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="text-left">
                      <th className="label py-2">Date</th><th className="label py-2">Type</th>
                      <th className="label py-2 text-right">Amount</th><th className="label py-2 text-right">Balance</th><th className="label py-2">Note</th>
                      {isCompanyAdmin && <th className="label py-2 text-right">Actions</th>}
                    </tr></thead>
                    <tbody>
                      {txns.map((t) => (
                        <tr key={t.id} className="border-t border-border" data-testid={`txn-${t.id}`}>
                          <td className="py-2.5">{fmtDate(t.created_at)}</td>
                          <td className="py-2.5"><Pill variant={t.type === "DEPOSIT" ? "success" : t.type === "INTEREST" ? "info" : "warning"}>{t.type}</Pill></td>
                          <td className="py-2.5 text-right tabular-nums">{fmt(t.amount)}</td>
                          <td className="py-2.5 text-right tabular-nums">{fmt(t.balance_after)}</td>
                          <td className="py-2.5 text-muted-foreground">{t.note || "—"}</td>
                          {isCompanyAdmin && (
                            <td className="py-2.5 text-right whitespace-nowrap">
                              <button className="btn-ghost text-xs px-2 py-1" onClick={() => editTxn(t)} data-testid={`edit-txn-${t.id}`}>Edit</button>
                              <button className="btn-ghost text-xs px-2 py-1 text-destructive" onClick={() => deleteTxn(t)} data-testid={`delete-txn-${t.id}`}>Delete</button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>
            </>
          ) : (
            <Section title="Select an account" testId="select-account-hint">
              <div className="text-sm text-muted-foreground py-12 text-center">Choose a savings account on the left to view transactions.</div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}
